/**
 * THIS STATION'S DISHES, and which have run out.
 *
 * Read from the published menu (only this station's) and the availability
 * list; written as a snooze, which every till already obeys within seconds.
 * The reason is always "run out" — that is the only thing a pass knows.
 */

export interface StationDish {
  readonly itemRef: string;
  readonly name: string;
  readonly soldOut: boolean;
}

interface Wire {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly fetch?: typeof fetch;
}

const read = async <T>(args: Wire, path: string): Promise<T> => {
  const response = await (args.fetch ?? fetch)(`${args.baseUrl}${path}`, {
    headers: { authorization: `Bearer ${args.apiKey}` },
  });
  if (!response.ok) throw new Error(`${path} answered ${response.status}`);
  return (await response.json()) as T;
};

export async function fetchStationDishes(args: Wire & { readonly stationId: string }): Promise<StationDish[]> {
  const venue = encodeURIComponent(args.venueId);
  const [menu, availability] = await Promise.all([
    read<{ items?: { itemRef: string; name: string; stationId: string }[] }>(args, `/v1/menu?venueId=${venue}`),
    read<{ unavailable?: { ref: string }[] }>(args, `/v1/menu/availability?venueId=${venue}`),
  ]);
  const off = new Set((availability.unavailable ?? []).map((u) => u.ref));
  return (menu.items ?? [])
    .filter((i) => i.stationId === args.stationId)
    .map((i) => ({ itemRef: i.itemRef, name: i.name, soldOut: off.has(i.itemRef) }));
}

export async function setSoldOut(args: Wire & { readonly itemRef: string; readonly soldOut: boolean }): Promise<void> {
  const venue = encodeURIComponent(args.venueId);
  const headers = {
    authorization: `Bearer ${args.apiKey}`,
    "content-type": "application/json",
    "idempotency-key": crypto.randomUUID(),
  };
  const response = args.soldOut
    ? await (args.fetch ?? fetch)(`${args.baseUrl}/v1/menu/snooze?venueId=${venue}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ref: args.itemRef, reason: "OUT_OF_STOCK" }),
      })
    : await (args.fetch ?? fetch)(
        `${args.baseUrl}/v1/menu/snooze?venueId=${venue}&ref=${encodeURIComponent(args.itemRef)}`,
        { method: "DELETE", headers },
      );
  if (!response.ok) throw new Error(`the menu service answered ${response.status}`);
}
